// v2.20.2 · tarjeta CLÍNICA del mosaico: residente, cama, estado, FC/FR y sus mini curvas vivas.
// Lo técnico (hora del equipo, calidad, atribución) vive en el tooltip y en la ficha.
import { HeartPulse, Wind, Radio } from "lucide-react";
import { MiniCurva } from "@/components/ekh/CurvasEkh";
import useCurvaViva from "@/hooks/useCurvaViva";
import { valorFrecuencia, edadTexto, conSenal, estadoCama, lecturaReciente } from "@/lib/ekh/vista";

function Vital({ icono: Icono, label, unidad, m, muestras, color, testId, curvaTestId }) {
  const v = valorFrecuencia(m);
  return (
    <div data-testid={testId} data-calidad={m?.calidad || "sin_dato"}
      title={`${label}: ${v.leyenda}. Recepción: ${m?.received_at || "—"}.`}
      className="min-w-0 rounded-lg bg-[color:var(--gv-raised,#eef2f7)] px-2 py-1.5">
      <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        <Icono className="h-3 w-3 shrink-0" />{label}
      </p>
      <p className="flex items-baseline gap-1">
        <span className={`truncate font-extrabold tabular-nums ${v.dato ? "text-2xl text-slate-800 dark:text-slate-100" : "text-xs text-slate-400"}`}>
          {v.texto}
        </span>
        {v.dato && <span className="text-[11px] font-semibold text-slate-400">{unidad}</span>}
      </p>
      <MiniCurva muestras={muestras} color={color} testId={curvaTestId}
        etiqueta={`Mini curva de ${label}`} />
    </div>
  );
}

export default function CamaEkhCard({ fila, seleccionada, onSeleccionar, curvaActiva = false,
  lecturaActiva = true }) {
  const estado = estadoCama(fila);
  const senal = conSenal(fila);
  const camaId = fila.cama_id || null;
  // ID4 · si la ficha de esta cama está abierta, ambas comparten UNA lectura (ver useCurvaViva).
  // R02 · la identidad del periodo (equipo, asignación, versión) reinicia la curva si cambia.
  const identidad = [fila.device_id ?? "", fila.asignacion?.id ?? "", fila.asignacion?.version ?? ""].join("|");
  const { vivo } = useCurvaViva(camaId, {
    activo: curvaActiva && !!camaId && lecturaActiva, intervaloMs: 2000, ventana: 90, identidad });
  const fcEdad = fila.instantaneas?.heart_rate?.edad_seg;
  // «EKH en línea» exige señal RECIENTE: una lectura de hace horas no se anuncia como en línea.
  const ultima = vivo?.t?.length ? Date.parse(vivo.t[vivo.t.length - 1]) : null;
  const curvaReciente = Number.isFinite(ultima) && (Date.now() - ultima) / 1000 <= 10;
  const enLinea = lecturaReciente(fila) || curvaReciente;
  const nombre = fila.persona_nombre || "Sin residente acreditado";
  const ubicacion = [fila.cama_etiqueta || fila.equipo, fila.habitacion].filter(Boolean).join(" · ");

  return (
    <button type="button" data-testid={`ekh-cama-${camaId || fila.device_id}`}
      data-device={fila.device_id}
      data-presencia={fila.instantaneas?.presence?.valor == null ? "desconocida" : (fila.instantaneas.presence.valor ? "si" : "no")}
      data-senal={senal ? "si" : "no"}
      data-estado={estado.clave}
      data-seleccionada={seleccionada ? "si" : "no"}
      onClick={() => onSeleccionar(fila)}
      className={`gv2-card w-full rounded-xl p-2.5 text-left transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3] ${seleccionada ? "ring-2 ring-[#0A84FF]" : ""} ${senal ? "" : "opacity-70 hover:opacity-100"}`}>
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={`truncate text-sm font-extrabold ${fila.persona_nombre ? "text-slate-800 dark:text-slate-100" : "text-slate-400"}`}>
            {nombre}
          </p>
          <p className="truncate text-[11px] text-slate-500">{ubicacion || "Sin ubicación"}</p>
        </div>
        <span data-testid={`ekh-estado-${fila.device_id}`}
          className={`gv2-chip shrink-0 ${estado.tono}`}>{estado.texto}</span>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-1.5">
        <Vital icono={HeartPulse} label="FC" unidad="lpm" color="#FF453A"
          m={fila.instantaneas?.heart_rate} muestras={vivo?.heart || []}
          testId={`ekh-fc-${fila.device_id}`} curvaTestId={`ekh-mini-fc-${fila.device_id}`} />
        <Vital icono={Wind} label="FR" unidad="rpm" color="#0A84FF"
          m={fila.instantaneas?.respiratory_rate} muestras={vivo?.resp || []}
          testId={`ekh-fr-${fila.device_id}`} curvaTestId={`ekh-mini-fr-${fila.device_id}`} />
      </div>

      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span data-testid={`ekh-linea-${fila.device_id}`}
          className={`gv2-chip inline-flex items-center ${enLinea ? "gv2-chip-live" : "gv2-chip-neutral"}`}>
          <Radio className="h-3 w-3" />{enLinea ? "EKH en línea" : "EKH sin señal"}
        </span>
        <span className="truncate text-[10px] text-slate-400">
          {fcEdad != null ? edadTexto(fcEdad) : ""}
        </span>
      </div>
    </button>
  );
}
